import { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { ApiError, campaignApi, type CampaignPage, type CampaignSection } from "@/lib/api";
import { productAnchors } from "@/lib/campaign";
import { Seo } from "@/components/Seo";
import { Header } from "@/components/layout/Header";
import { MinimalFooter } from "@/components/layout/Footer";
import { Hero } from "@/components/sections/Hero";
import { OfferBanner } from "@/components/sections/OfferBanner";
import { PromoBanner } from "@/components/sections/PromoBanner";
import { Offices } from "@/components/sections/Offices";
import { Products } from "@/components/sections/Products";
import { Reviews } from "@/components/sections/Reviews";
import { SetupInspiration } from "@/components/sections/SetupInspiration";
import { CatalogProducts } from "@/components/shop/CatalogProducts";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";

/**
 * A campaign page, /lp/:slug (DEL-437): the landing page an ad points at.
 *
 * It's built from the home page's own sections, in the order and with the
 * words the admin chose, so it looks like Kanata without a developer. The
 * header is the site's own, navigation and announcement bar included (the
 * client's call, 4 Nov: it was the logo and the cart only). The footer stays
 * reduced and there's no popup: someone who clicked an ad came for the offer.
 *
 * Client-rendered on one prerendered shell (/lp/_shell, served for every
 * /lp/* by the Worker), since pages are authored after a deploy. Always noindex: an ad's landing
 * page shouldn't compete with the real pages in search, and ?preview opens
 * drafts.
 */
export default function Campaign() {
  const { slug = "" } = useParams();
  const [params] = useSearchParams();
  const preview = params.get("preview") ?? undefined;
  const [page, setPage] = useState<CampaignPage | null>(null);
  const [missing, setMissing] = useState(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let live = true;
    setPage(null);
    setMissing(false);
    setFailed(false);
    campaignApi
      .get(slug, preview)
      .then((p) => live && setPage(p))
      .catch((e) => {
        if (!live) return;
        if (e instanceof ApiError && e.status === 404) setMissing(true);
        else setFailed(true);
      });
    return () => {
      live = false;
    };
  }, [slug, preview]);

  const sections = page?.sections ?? [];
  const anchors = productAnchors(sections);
  const overHero = sections[0]?.type === "hero";

  return (
    <>
      <Seo
        title={page ? `${page.title} — Kanata Indonesia` : "Kanata Indonesia"}
        description={page?.description || "Standing desk elektrik Kanata, dibuat di Indonesia."}
        path={`/lp/${slug}`}
        noindex
      />
      <Header overHero={overHero} />
      {page?.status === "draft" && (
        <p className="fixed inset-x-0 bottom-0 z-40 bg-ink py-2 text-center text-sm text-white">
          Pratinjau draft. Pengunjung belum bisa melihat halaman ini.
        </p>
      )}
      <main className={overHero ? undefined : "pt-28"}>
        {missing || failed ? (
          <div className="mx-auto max-w-7xl px-5 py-32 sm:px-8">
            <h1 className="text-2xl font-medium">
              {missing ? "Halaman ini sudah tidak tersedia." : "Halaman gagal dimuat. Silakan muat ulang."}
            </h1>
            <Button asChild className="mt-6">
              <Link to="/katalog">Lihat semua produk</Link>
            </Button>
          </div>
        ) : !page ? (
          <div className="mx-auto max-w-7xl space-y-6 px-5 py-28 sm:px-8" role="status" aria-label="Memuat halaman">
            <Skeleton className="h-[50vh] w-full rounded-3xl" />
            <Skeleton className="h-8 w-1/3" />
          </div>
        ) : (
          sections.map((s, i) => <Section key={i} section={s} anchor={anchors.get(i)} />)
        )}
      </main>
      <MinimalFooter />
    </>
  );
}

function Section({ section: s, anchor }: { section: CampaignSection; anchor?: string }) {
  switch (s.type) {
    case "hero":
      return <Hero authored={s.hero} />;
    case "offer":
      return <OfferBanner offer={s.offer} />;
    case "promo":
      return <PromoBanner authored={s.promo} />;
    case "products":
      return (
        <section id={anchor} className="mx-auto max-w-7xl scroll-mt-20 px-5 py-16 sm:px-8">
          {s.products.title && <h2 className="text-3xl font-medium">{s.products.title}</h2>}
          <CatalogProducts line={s.products.line || undefined} slugs={s.products.slugs} />
        </section>
      );
    case "range":
      return <Products id={anchor} />;
    case "reviews":
      return <Reviews />;
    case "setup":
      return <SetupInspiration />;
    case "offices":
      return <Offices />;
  }
}
